/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/** Formatting shared by the Account 360 tiles and lists. */

import { v2Tones } from "styles/tokens/v2";

/** "A$12.4K" style, falling back to plain digits for an unknown code. */
export function formatMoney(amount, currency, { compact = false } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      notation: compact ? "compact" : "standard",
      maximumFractionDigits: compact ? 1 : 2,
    }).format(n);
  } catch (e) {
    return `${currency || ""} ${n.toLocaleString()}`.trim();
  }
}

/**
 * A grouped money list ([{currency, amount}]) as one headline figure: the
 * account's own currency when present, else the largest, plus how many
 * other currencies sit behind it.
 */
export function headlineMoney(list, preferred) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const main = list.find((m) => m.currency === preferred) || list[0];
  return { main, others: list.filter((m) => m !== main) };
}

/** "3 days ago" / "in 2 hours", or a date when it is far away. */
export function relativeTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diff = (date.getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, secs] of units) {
    if (abs >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return "just now";
}

// Checked in order: "Unpaid" and "Partially Paid" must not read as paid,
// nor "Inactive" as active.
const TONE_BY_STATUS = [
  [/inactive/i, "neutral"],
  [/lost|cancel|reject|fail|overdue|declined|terminated|expired/i, "danger"],
  [/unpaid|not paid|partial|pending|draft|hold|waiting|due|scheduled|proposal|negotiation/i, "warning"],
  [/won|paid|complete|confirmed|active|accepted|resolved|delivered/i, "success"],
  [/open|new|progress|qualif|issued|sent/i, "info"],
];

/** A status word's badge tone ({bg, fg} from v2Tones). */
export function toneFor(status) {
  const s = String(status || "");
  const hit = TONE_BY_STATUS.find(([re]) => re.test(s));
  return v2Tones[hit ? hit[1] : "neutral"];
}
